import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Autocomplete, Button, TextField } from "@mui/material";

import { Dataset } from "../../api/types/datasets";
import { Region } from "../../api/types/regions";
import { PageHeader } from "../../components/PageHeader";
import { PageSection } from "../../components/PageSection";
import { useDataSets } from "../dashboard-view/api/useDatasets";

import { useExportPrices } from "./api/useExportPrices";
import { useFetchRegions } from "./api/useFetchRegions";
import { ProductsWithPrices } from "./components/ProductsWithPrices";

export type PriceValue = {
  HighPricePKg?: number | null | undefined;
  PriceLowOfHigh?: number | null | undefined;
};

export type PriceItem = {
  name: string;
  productId: number | undefined | null;
  world: PriceValue;
  region: PriceValue;
};

export type PricePayload = {
  productId: number | undefined | null;
  data: {
    highPricePKg: number | null | undefined;
    priceLowOfHighFraction: number | null | undefined;
  };
};

export function PriceView() {
  const { regions } = useFetchRegions();
  const { datasets } = useDataSets();
  const [selectedRegion, setSelectedRegion] = useState<Region | null>();
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const { formatMessage } = useIntl();
  const { exportPrices, isPending } = useExportPrices();

  const regionsOptions = useMemo(
    () => (regions?.Regions || []).filter(r => !r.IsDefault),
    [regions],
  );

  const canExport = !!selectedRegion?.Id && !!selectedDataset?.Id && !isPending;

  const handleExport = () => {
    if (!selectedRegion?.Id || !selectedDataset?.Id) return;
    exportPrices({
      regionId: selectedRegion.Id,
      datasetId: selectedDataset.Id,
    });
  };

  return (
    <div>
      <PageHeader
        text={formatMessage({
          id: "PRICE.TITLE",
          defaultMessage: "Price",
          description: "Price page title",
        })}
      />

      <PageSection title="Select region">
        <Autocomplete
          size="small"
          value={selectedRegion}
          onChange={(e, value) => setSelectedRegion(value)}
          options={regionsOptions || []}
          getOptionLabel={option => option.Name ?? ""}
          renderInput={params => (
            <TextField {...params} label="Choose a region" />
          )}
        />
      </PageSection>

      <PageSection title="Select dataset">
        <div className="flex flex-row items-center gap-4">
          <Autocomplete
            className="flex-1"
            size="small"
            value={selectedDataset}
            onChange={(e, value) => setSelectedDataset(value)}
            options={datasets?.Datasets ?? []}
            getOptionLabel={option => option.Name ?? ""}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            renderInput={params => (
              <TextField
                {...params}
                label={formatMessage({
                  id: "APP.DATASET",
                  defaultMessage: "Dataset",
                  description: "Dataset label",
                })}
              />
            )}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!canExport}
            onClick={handleExport}>
            {formatMessage({
              id: "PRICE.EXPORT_TO_EXCEL",
              defaultMessage: "Export to Excel",
              description: "Price export to Excel button",
            })}
          </Button>
        </div>
      </PageSection>

      <PageSection title="Products with prices">
        <ProductsWithPrices selectedRegion={selectedRegion} />
      </PageSection>
    </div>
  );
}
